import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/20/solid';

import { routeToPage } from 'support/helpers/navigation/navigation';
import { ConnectionsRoutes } from 'support/types';
import { DocumentType } from 'support/types/dtos';
import portalSchema from 'support/types/schema-portal';
import { BadgeList } from 'components/BadgeList/BadgeList';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { SlideOver } from 'components/Display/SlideOver/SlideOver';
import { NotificationRoutingBadge } from 'components/NotificationRoutingBadge/NotificationRoutingBadge';
import { PartnerLogo } from 'components/PartnerLogo/PartnerLogo';
import { SearchInput } from 'components/SearchInput/SearchInput';

type RelationshipResponsibleUsers = portalSchema.components['schemas']['RelationshipResponsibleUsers'];

type RelationshipOverridesPanelProps = {
  open: boolean;
  onClose: () => void;
  documentType: DocumentType | null;
  relationships: Array<RelationshipResponsibleUsers>;
  currentPartnerId: string;
};

export const RelationshipOverridesPanel = ({
  open,
  onClose,
  documentType,
  relationships,
  currentPartnerId,
}: RelationshipOverridesPanelProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (open) {
      setFilter('');
    }
  }, [open]);

  const filteredRelationships = relationships.filter((rel) => {
    if (!filter) {
      return true;
    }
    const lowerFilter = filter.toLowerCase();

    const otherPartner = currentPartnerId === rel.sender_partner_id ? rel.receiver_partner : rel.sender_partner;

    const matchesName = otherPartner?.name?.toLowerCase().includes(lowerFilter);
    const matchesEmail = rel.responsible.some((user) => user.email.toLowerCase().includes(lowerFilter));

    return matchesName || matchesEmail;
  });

  return (
    <SlideOver
      open={open}
      setOpen={onClose}
      size="large"
      title={t('settings:notificationsManagement.panel.title', {
        documentType: t(`common:messageTypes.plural.${documentType}`),
      })}
      subtitle={t('settings:notificationsManagement.panel.subtitle', {
        count: relationships.length,
      })}
    >
      <div className="flex flex-col gap-4">
        <SearchInput
          filter={filter}
          onFilterChange={setFilter}
          placeholder={t('settings:notificationsManagement.panel.searchPlaceholder')}
        />

        <div className="divide-y divide-gray-200">
          {filteredRelationships.map((rel) => {
            const isSender = currentPartnerId === rel.sender_partner_id;
            const otherPartner = isSender ? rel.receiver_partner : rel.sender_partner;

            // t('settings:notificationsManagement.panel.sending')
            // t('settings:notificationsManagement.panel.receiving')
            return (
              <div key={rel.id} className="flex items-center justify-between gap-4 py-3">
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <PartnerLogo imageUrl={otherPartner?.logoUrl} size="small" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-gray-900">{otherPartner?.name}</span>
                      <Badge color={BadgeColor.gray}>
                        {t(
                          isSender
                            ? 'settings:notificationsManagement.panel.sending'
                            : 'settings:notificationsManagement.panel.receiving',
                        )}
                      </Badge>
                    </div>
                    <div className="mt-1">
                      <BadgeList values={rel.responsible.map((user) => user.email)} emptyLabel="" />
                    </div>
                    <NotificationRoutingBadge notificationResolving={rel.notification_resolving} className="mt-1" />
                  </div>
                </div>
                <Link
                  to={routeToPage(ConnectionsRoutes.relationshipDetails, {
                    partnerId: otherPartner?.id ?? '',
                    relationshipId: rel.id,
                  })}
                  className="flex shrink-0 items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800"
                >
                  {t('settings:notificationsManagement.panel.connectionDetails')}
                  <ChevronRightIcon className="size-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </div>
    </SlideOver>
  );
};
